import { format, getHours, getMinutes, setHours, setMinutes } from 'date-fns';
import { Image } from 'expo-image';
import {
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { DateTime } from 'luxon';
import React from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useJooserBooking } from '@/api/customers/use-jooser-booking';
import { useCreateJooserAppointment } from '@/api/reservations/use-create-jooser-appointment';
import type { CreateJooserIntent } from '@/api/reservations/use-create-jooser-intent';
import { useCreateJooserIntent } from '@/api/reservations/use-create-jooser-intent';
import type { JooserAppointmentType } from '@/api/reservations/use-jooser-appointment-types';
import { useJooserAppointmentTypes } from '@/api/reservations/use-jooser-appointment-types';
import { useJooserAvailableTimeslots } from '@/api/reservations/use-jooser-available-timeslots';
import { useJooserCancelAppointment } from '@/api/reservations/use-jooser-cancel-appointment';
import { useJooserValidAddressRequest } from '@/api/reservations/use-jooser-valid-address-request';
import { useReservation } from '@/api/reservations/use-reservation';
import type { Address } from '@/api/subscriptions/types';
import { Button, Card, Checkbox, Input, showErrorMessage, Text } from '@/ui';
import { Calendar } from '@/ui/calendar';
import { Chip, ChipType } from '@/ui/chip';
import GoogleAutocomplete from '@/ui/google-autocomplete';
import { Icon } from '@/ui/icon';
import { ArrowRight, Search } from '@/ui/icons';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';
import { Chat } from '@/ui/icons/chat';
import { Location } from '@/ui/icons/location';
import { InputMulti } from '@/ui/input-multi';
import { Loader } from '@/ui/loader';
import { ProgressIndicator } from '@/ui/progress-indicator';

import BackButton from '../back-button';
import PdfViewer from '../pdf-viewer';
import RechargePayment from './recharge-payment';
import { ServiceCard } from './service-card';

const now = new Date();

export default function Recharge({ id }: { id: number }) {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const local = useLocalSearchParams();
  const [currentStep, setCurrentStep] = React.useState(1);
  const [viewTOS, setViewTOS] = React.useState(false);
  const [appointmentType, setAppointmentType] =
    React.useState<JooserAppointmentType | null>(null);
  const [showDatetimeCalendar, setShowDatetimeCalendar] = React.useState(false);
  const [datetime, setDatetime] = React.useState<Date>();
  const [showGoogleAutoComplete, setShowGoogleAutoComplete] =
    React.useState(false);
  const [address, setAddress] = React.useState<Address>();
  const [carLocationInstructions, setCarLocationInstructions] =
    React.useState<string>();
  const [jooserIntent, setJooserIntent] =
    React.useState<CreateJooserIntent | null>();
  const [isHandlingPaymentPending, setIsHandlingPaymentPending] =
    React.useState(false);
  const [isHandlingPaymentSuccess, setIsHandlingPaymentSuccess] =
    React.useState(false);
  const [isHandlingPaymentError, setIsHandlingPaymentError] =
    React.useState(false);
  const [jooserTocAgreed, setJooserTocAgreed] = React.useState(false);

  const {
    data: resData,
    isFetching: isFetchingReservation,
    refetch,
  } = useReservation({
    //@ts-ignore
    variables: { id: id },
    enabled: false,
  });

  const {
    data: appointmentTypesData,
    isFetching: isFetchingAppointmentTypes,
    refetch: refetchAppointmentTypes,
  } = useJooserAppointmentTypes({
    //@ts-ignore
    variables: { id: id },
    enabled: !!id && !local.appointmentId,
  });

  const {
    data: availableTimeslotsData,
    isFetching: isFetchingAvailableTimeslots,
    refetch: refetchAvailableTimeslots,
  } = useJooserAvailableTimeslots({
    //@ts-ignore
    variables: {
      id: id,
      appointmentTypeId: appointmentType?.id,
      startDate: now,
      endDate: new Date(resData?.endTime),
    },
    enabled: !!appointmentType && !!resData && !local.appointmentId,
  });

  const { data: jooserBookingData, isFetching: isFetchingJooserBooking } =
    useJooserBooking({
      //@ts-ignore
      variables: { id: local.appointmentId },
      enabled: !!local.appointmentId,
    });

  const {
    data: jooserValidAddressData,
    isFetching: isFetchingJooserValidAddress,
  } = useJooserValidAddressRequest({
    //@ts-ignore
    variables: {
      id: id,
      latitude: address?.latitude,
      longitude: address?.longitude,
    },
    enabled: !!id && !!address && !local.appointmentId,
  });

  const { mutateAsync: createJooserIntent, isPending: isCreatingJooserIntent } =
    useCreateJooserIntent();

  const {
    mutateAsync: createJooserAppointment,
    isPending: isCreatingJooserAppointment,
    isSuccess: isCreateJooserAppointmentSuccess,
  } = useCreateJooserAppointment();

  const {
    mutateAsync: cancelJooserAppointment,
    isPending: isCancellingJooserAppointment,
    isSuccess: isCancelJooserAppointmentSuccess,
  } = useJooserCancelAppointment();

  useFocusEffect(
    React.useCallback(() => {
      refetch();
    }, [refetch])
  );

  React.useEffect(() => {
    if (!local.appointmentId || !jooserBookingData) {
      return;
    }

    const dt = DateTime.fromISO(jooserBookingData.datetime, {
      setZone: true,
    }).setZone(jooserBookingData.timezone);

    if (dt.isValid) {
      setDatetime(dt.toJSDate());
    }
  }, [jooserBookingData, local.appointmentId]);

  const calendarDisabledDateIds = React.useMemo(() => {
    if (!availableTimeslotsData) {
      return [];
    }

    const disabledDateIds: string[] = [];

    Object.keys(availableTimeslotsData).forEach((date: string) => {
      if (availableTimeslotsData[date].length === 0) {
        // our client converts all keys to camel so '20260801' becomes '20260801'
        disabledDateIds.push(
          date.replace(/^(\d{4})(\d{2})(\d{2})$/, '$1-$2-$3')
        );
      }
    });

    return disabledDateIds;
  }, [availableTimeslotsData]);

  // first available date+time from the backend, used as the calendar's default
  // selection when the user hasn't picked one yet. Keys are 'YYYYMMDD' (dashes
  // stripped by the api client's key normalizer) and times are 'HH:mm'.
  const firstAvailableDatetime = React.useMemo<Date | undefined>(() => {
    if (!availableTimeslotsData) return undefined;

    for (const [dateKey, times] of Object.entries(availableTimeslotsData)) {
      if (!times || times.length === 0) continue;

      const year = Number(dateKey.slice(0, 4));
      const month = Number(dateKey.slice(4, 6)) - 1;
      const day = Number(dateKey.slice(6, 8));
      const [hours, minutes] = times[0].split(':').map(Number);

      return new Date(year, month, day, hours, minutes, 0, 0);
    }

    return undefined;
  }, [availableTimeslotsData]);

  const handleNext = async () => {
    if (currentStep === 2) {
      try {
        const res = await createJooserIntent({
          id: resData!.id,
          amount: parseFloat(appointmentType!.price),
        });

        setJooserIntent(res);
      } catch (error) {
        showErrorMessage('Unable to create payment intent. Please try again.');
        return;
      }
    }

    setCurrentStep(currentStep + 1);
  };

  const handleCancel = async () => {
    try {
      await cancelJooserAppointment({
        id: resData!.id,
        appointmentTypeId: jooserBookingData!.id,
      });

      Toast.show({
        text1: 'On-demand charging cancelled successfully',
        type: 'success',
      });

      router.back();
    } catch (e: any) {
      Toast.show({
        text1: 'Error cancelling on-demand charging',
        type: 'error',
      });
    }
  };

  const handlePaymentSuccessful = async () => {
    try {
      await createJooserAppointment({
        id: resData!.id,
        appointmentTypeId: appointmentType!.id,
        datetime: DateTime.fromJSDate(datetime!)
          .setZone('America/New_York', {
            keepLocalTime: true,
          })
          .toJSDate(),
        address: address!.description,
        carLocationInstructions: carLocationInstructions!,
      });
    } catch (e: any) {
      showErrorMessage(e.message);
    }
  };

  const renderStep = () => {
    if (isCreateJooserAppointmentSuccess || local.appointmentId) {
      return (
        <View style={{ gap: 24 }}>
          {!local.appointmentId ? (
            <View style={{ gap: 16 }}>
              <Text type="title1">Recharge Reserved</Text>

              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                Your charging appointment has been successfully reserved.
              </Text>

              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                Jooser will contact you with any additional details.
              </Text>
            </View>
          ) : null}

          <View
            style={{
              backgroundColor: theme.colors.card.default.backgroundColor,
              borderRadius: 16,
              overflow: 'hidden',
            }}
          >
            <View
              style={{
                padding: 16,
                backgroundColor: theme.colors.card.default.backgroundColor,
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <View>
                <Text type="title2">Recharge Summary</Text>
              </View>

              <Chip
                type={ChipType.BRAND}
                text={`${
                  appointmentType?.duration || jooserBookingData?.duration
                } minutes`}
                iconWidth={8}
                iconHeight={8}
                style={{ alignSelf: 'center', width: 100 }}
              />
            </View>

            <View
              style={{
                padding: 16,
                backgroundColor: theme.colors.card.hover.backgroundColor,
                gap: 8,
              }}
            >
              <View style={{ flexDirection: 'row', gap: 4 }}>
                <Icon
                  type="dressed"
                  icon={<CalendarIcon />}
                  fillColor={theme.colors.icon.color}
                  width={16}
                  height={16}
                />

                <View>
                  <Text type="bodyRegular">Date & Time</Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {datetime ? format(datetime, 'MMMM d, yyyy h:mm a') : ''}
                  </Text>
                </View>
              </View>

              <View style={{ flexDirection: 'row', gap: 4 }}>
                <Icon
                  type="dressed"
                  icon={<Location />}
                  fillColor={theme.colors.icon.color}
                  width={16}
                  height={16}
                />

                <View style={{ flexShrink: 1 }}>
                  <Text type="bodyRegular">Location</Text>

                  <Text
                    type="bodyRegular"
                    style={{
                      color: theme.colors.text.secondary,
                    }}
                  >
                    {address
                      ? address.description
                      : jooserBookingData?.location}
                  </Text>
                </View>
              </View>

              <View style={{ flexDirection: 'row', gap: 4 }}>
                <Icon
                  type="dressed"
                  icon={<Chat />}
                  fillColor={theme.colors.icon.color}
                  width={16}
                  height={16}
                />

                <View style={{ flexShrink: 1 }}>
                  <Text type="bodyRegular">Car location</Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {carLocationInstructions ||
                      jooserBookingData?.direction ||
                      ''}
                  </Text>
                </View>
              </View>
            </View>

            <View
              style={{
                padding: 16,
                backgroundColor: theme.colors.card.default.backgroundColor,
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <View>
                <Text type="bodyRegular">
                  {appointmentType
                    ? `${appointmentType.duration} minute charge`
                    : jooserBookingData?.type.split('EON—  ')[1]}
                </Text>
              </View>

              <View>
                <Text type="bodyRegular">
                  $
                  {appointmentType
                    ? appointmentType.price
                    : jooserBookingData?.price}
                </Text>
              </View>
            </View>

            <View
              style={{
                padding: 16,
                backgroundColor: theme.colors.card.hover.backgroundColor,
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <View>
                <Text type="title2">Total</Text>
              </View>

              <View>
                <Text type="title2">
                  $
                  {appointmentType
                    ? appointmentType.price
                    : jooserBookingData?.price}
                </Text>
              </View>
            </View>
          </View>
        </View>
      );
    }

    if (currentStep === 3) {
      return (
        <View style={{ flexGrow: 1 }}>
          <View
            style={{
              backgroundColor: theme.colors.card.default.backgroundColor,
              borderRadius: 16,
              overflow: 'hidden',
            }}
          >
            <View
              style={{
                padding: 16,
                backgroundColor: theme.colors.card.default.backgroundColor,
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <View>
                <Text type="title2">Recharge Summary</Text>
              </View>

              <Chip
                type={ChipType.BRAND}
                text={`${appointmentType?.duration} minutes`}
                iconWidth={8}
                iconHeight={8}
                style={{ alignSelf: 'center', width: 100 }}
              />
            </View>

            <View
              style={{
                padding: 16,
                backgroundColor: theme.colors.card.hover.backgroundColor,
                gap: 8,
              }}
            >
              <View style={{ flexDirection: 'row', gap: 4 }}>
                <Icon
                  type="dressed"
                  icon={<CalendarIcon />}
                  fillColor={theme.colors.icon.color}
                  width={16}
                  height={16}
                />

                <View>
                  <Text type="bodyRegular">Date & Time</Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {datetime ? format(datetime, 'MMMM d, yyyy h:mm a') : ''}
                  </Text>
                </View>
              </View>

              <View style={{ flexDirection: 'row', gap: 4 }}>
                <Icon
                  type="dressed"
                  icon={<Location />}
                  fillColor={theme.colors.icon.color}
                  width={16}
                  height={16}
                />

                <View style={{ flexShrink: 1 }}>
                  <Text type="bodyRegular">Location</Text>

                  <Text
                    type="bodyRegular"
                    style={{
                      color: theme.colors.text.secondary,
                    }}
                  >
                    {address ? address.description : ''}
                  </Text>
                </View>
              </View>

              <View style={{ flexDirection: 'row', gap: 4 }}>
                <Icon
                  type="dressed"
                  icon={<Chat />}
                  fillColor={theme.colors.icon.color}
                  width={16}
                  height={16}
                />

                <View style={{ flexShrink: 1 }}>
                  <Text type="bodyRegular">Car location</Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {carLocationInstructions || ''}
                  </Text>
                </View>
              </View>
            </View>

            <View
              style={{
                padding: 16,
                backgroundColor: theme.colors.card.default.backgroundColor,
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <View>
                <Text type="bodyRegular">
                  {appointmentType
                    ? `${appointmentType.duration} minute charge`
                    : ''}
                </Text>
              </View>

              <View>
                <Text type="bodyRegular">
                  ${appointmentType ? appointmentType.price : ''}
                </Text>
              </View>
            </View>

            <View
              style={{
                padding: 16,
                backgroundColor: theme.colors.card.hover.backgroundColor,
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <View>
                <Text type="title2">Total Due Now</Text>
              </View>

              <View>
                <Text type="title2">
                  ${appointmentType ? appointmentType.price : ''}
                </Text>
              </View>
            </View>
          </View>
        </View>
      );
    }

    if (currentStep === 2) {
      return (
        <View style={{ gap: 16, zIndex: 1 }}>
          <View style={{ zIndex: 1 }}>
            <Pressable
              onPress={() => {
                setShowDatetimeCalendar(!showDatetimeCalendar);
              }}
            >
              <Input
                label="Select a date and time"
                placeholder={
                  datetime
                    ? format(datetime, 'MMMM d, yyyy h:mm a')
                    : 'Select a date and time'
                }
                leftIcon={
                  <Icon
                    type="naked"
                    icon={<CalendarIcon />}
                    fillColor={theme.colors.icon.color}
                    width={22}
                    height={22}
                  />
                }
                disabled
              />
            </Pressable>

            <Calendar
              mode="datetime"
              availableDatetimeSlots={availableTimeslotsData}
              disabledDateIds={calendarDisabledDateIds}
              isVisible={showDatetimeCalendar}
              minDate={new Date()}
              maxDate={resData ? new Date(resData.endTime) : undefined}
              defaultStartDate={datetime ?? firstAvailableDatetime}
              onHide={() => {
                setShowDatetimeCalendar(false);
              }}
              onDatesSelected={(startDt, _) => {
                setShowDatetimeCalendar(false);

                setDatetime(startDt);
              }}
              onDateSelected={(startDt) => {
                const startDate = datetime || new Date();
                const formattedStartDatetime = setMinutes(
                  setHours(startDt, getHours(startDate)),
                  getMinutes(startDate)
                );

                setDatetime(formattedStartDatetime);
              }}
              onTimeSelected={(startDt) => {
                setDatetime(startDt);

                setShowDatetimeCalendar(false);
              }}
              range={false}
              style={{
                zIndex: 1,
                position: 'absolute',
                bottom: 100,
                right: 550,
              }}
              containerStyle={{
                borderWidth: 1,
                borderColor: theme.colors.card.default.borderColor,
                shadowColor: theme.colors.card.default.borderColor,
                shadowOffset: {
                  width: 0,
                  height: 5,
                },
                shadowOpacity: 0.5,
                shadowRadius: 5,
                elevation: 10,
              }}
              id="datetime-calendar"
              hasBackdrop={false}
              coverScreen={false}
            />
          </View>

          <View
            style={[
              {
                flexGrow: showGoogleAutoComplete ? 1 : 0,
                zIndex: showGoogleAutoComplete ? 3 : 1,
                gap: 4,
              },
            ]}
          >
            {!showGoogleAutoComplete ? (
              <Input
                label="Select a location"
                placeholder={address ? address.description : 'Search address'}
                value={address ? address.description : ''}
                onChangeText={() => {}}
                onFocus={() => {
                  setShowGoogleAutoComplete(true);
                }}
                leftIcon={
                  <Icon
                    type="naked"
                    icon={<Search />}
                    fillColor={theme.colors.icon.color}
                    width={22}
                    height={22}
                  />
                }
              />
            ) : null}

            {showGoogleAutoComplete ? (
              <GoogleAutocomplete
                label="Select a location"
                onPress={(d: Address) => {
                  setAddress({
                    description: d.description,
                    placeId: d.placeId,
                    latitude: d.latitude,
                    longitude: d.longitude,
                    city: d.city,
                    state: d.state,
                    zipcode: d.zipcode,
                  });

                  setShowGoogleAutoComplete(false);
                }}
                style={{ width: '100%' }}
                autoFocus
              />
            ) : null}

            {jooserValidAddressData === false ? (
              <Text style={{ color: theme.colors.error }}>
                Location needs to be in NYC Tri-State area
              </Text>
            ) : null}
          </View>

          <View>
            <InputMulti
              label="Where can we find your car?"
              text={carLocationInstructions || ''}
              onChangeText={(s: string) => setCarLocationInstructions(s)}
              placeholder="e.g., behind the building in the parking garage"
              style={{ minHeight: 100 }}
            />
          </View>
        </View>
      );
    }

    return (
      <View style={{ gap: 24 }}>
        <Card>
          <View
            style={{
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.card.default.borderColor,
              padding: 16,
              gap: 16,
            }}
          >
            <Text type="title2">On-Demand Recharging</Text>

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Text
                type="caption1"
                style={{
                  color: theme.colors.text.secondary,
                }}
              >
                Powered by:
              </Text>

              <Image
                style={{
                  width: 37,
                  height: 24,
                }}
                source={require('@/../public/images/logos/jooser-logo.png')}
                contentFit="cover"
              />
            </View>
          </View>

          <View
            style={{
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.card.default.borderColor,
              padding: 16,
            }}
          >
            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              Get to 80% in 45 minutes. Jooser comes to you with a portable
              fast-charger. You don't need to be present during the session.
            </Text>
          </View>

          <View
            style={{
              padding: 16,
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Text type="bodyRegular">Terms and conditions & FAQ</Text>

            <Button
              text="View"
              type="primaryExtraSmall"
              onPress={async () => {
                setViewTOS(true);
              }}
              rightIcon={
                <Icon
                  type="naked"
                  icon={<ArrowRight />}
                  width={12}
                  height={12}
                  fillColor={theme.colors.button.primary.default.text}
                />
              }
            />
          </View>
        </Card>

        <View>
          <Text type="title2">Choose Your Service</Text>

          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary, width: '90%' }}
          >
            Select the recharge level that fits your needs
          </Text>
        </View>

        <View>
          {isFetchingAppointmentTypes ? <Loader /> : null}

          {appointmentTypesData?.map((aptType: JooserAppointmentType) => {
            return (
              <ServiceCard
                key={aptType.id}
                title="Standard Charge"
                description={`${aptType.duration} minute charging session`}
                price={`$${aptType.price}`}
                onPress={() => {
                  setAppointmentType(aptType);
                }}
                active={aptType.id === appointmentType?.id}
              />
            );
          })}
        </View>
      </View>
    );
  };

  if (
    isFetchingReservation ||
    !resData ||
    isCreatingJooserIntent ||
    isCreatingJooserAppointment ||
    isCancellingJooserAppointment ||
    isFetchingJooserBooking ||
    (currentStep === 2 && isFetchingAvailableTimeslots)
  ) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  return (
    <View style={{ flex: 1, zIndex: 1 }}>
      <Stack.Screen
        options={{
          headerLeft: () => (
            <BackButton
              onPress={() => {
                if (currentStep > 1) {
                  setCurrentStep(currentStep - 1);
                }

                if (currentStep === 1) {
                  router.back();
                }
              }}
            />
          ),
        }}
      />

      {currentStep < 3 && !local.appointmentId ? (
        <ProgressIndicator currentStep={currentStep} numSteps={3} />
      ) : null}

      <View style={{ flex: 1 }}>
        <ScrollView
          contentContainerStyle={{ flexGrow: 1, gap: 32 }}
          style={{ flex: 1 }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <View
            style={{
              flexGrow: 1,
              padding: 16,
              gap: 24,
            }}
          >
            {renderStep()}
          </View>
        </ScrollView>

        {currentStep !== 3 && !local.appointmentId ? (
          <View style={{ padding: 16 }}>
            <Button
              text="Next"
              type="primary"
              onPress={handleNext}
              disabled={
                (currentStep === 1 && !appointmentType) ||
                (currentStep === 2 &&
                  (!datetime ||
                    !address ||
                    !carLocationInstructions ||
                    !jooserValidAddressData))
              }
            />
          </View>
        ) : null}

        {!local.appointmentId && isCreateJooserAppointmentSuccess ? (
          <View style={{ padding: 16 }}>
            <Button
              text="Back To Trips"
              type="primary"
              onPress={() => {
                router.back();
              }}
            />
          </View>
        ) : null}

        {local.appointmentId ? (
          <View style={{ padding: 16, gap: 8 }}>
            <Button
              text="Back To Trip"
              type="primary"
              onPress={() => {
                router.back();
              }}
            />

            <Button
              text="Cancel On-Demand Charging"
              type="ghost"
              onPress={handleCancel}
              textStyle={{ color: theme.colors.error }}
            />
          </View>
        ) : null}

        {currentStep === 3 && !isCreateJooserAppointmentSuccess ? (
          <View style={{ padding: 16, gap: 16 }}>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <Checkbox
                active={jooserTocAgreed}
                onPress={() => {
                  setJooserTocAgreed(!jooserTocAgreed);
                }}
              />

              <View style={{ flexDirection: 'row' }}>
                <Text type="bodyRegular">
                  I accept the service provider's{' '}
                  <Pressable
                    onPress={() => {
                      setViewTOS(true);
                    }}
                  >
                    <Text
                      type="bodyRegular"
                      style={{ textDecorationLine: 'underline' }}
                    >
                      terms and conditions
                    </Text>
                  </Pressable>
                </Text>
              </View>
            </View>

            <RechargePayment
              appointmentType={appointmentType!}
              jooserIntent={jooserIntent!}
              onPaymentInitiated={() => {
                setIsHandlingPaymentPending(true);
                setIsHandlingPaymentError(false);
              }}
              onPaymentSucceeded={() => {
                setIsHandlingPaymentPending(false);
                setIsHandlingPaymentSuccess(true);

                handlePaymentSuccessful();
              }}
              onPaymentFailed={() => {
                setIsHandlingPaymentPending(false);
                setIsHandlingPaymentError(true);
              }}
              disabled={!jooserTocAgreed}
            />
          </View>
        ) : null}
      </View>

      <PdfViewer
        navTitle={'Jooser Terms and Conditions'}
        uri={'https://eon-app.s3.us-east-1.amazonaws.com/jooser/jooser-tos.pdf'}
        onDismiss={() => {
          setViewTOS(false);
        }}
        visible={viewTOS}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
  },
}));
